import dayjs from 'dayjs';

import { KontoDto, Situasjon, Tidsperiode, UttakPeriodeDto } from '@navikt/fp-types';
import { Uttaksdagen } from '@navikt/fp-utils';

import { sorterUttakPerioder } from '../periodeUtils';

const ikkeDeltUttakAdopsjonFarMedmor = ({
    famDato,
    foreldrepengerKonto,
    erMorUfør,
    aktivitetsfriKvote,
    bareFarMedmorHarRett,
    farOgFar,
}: {
    famDato: string;
    foreldrepengerKonto: KontoDto;
    erMorUfør: boolean | undefined;
    aktivitetsfriKvote: KontoDto | undefined;
    bareFarMedmorHarRett: boolean;
    farOgFar: boolean;
}): UttakPeriodeDto[] => {
    const førsteUttaksdag = Uttaksdagen.denneEllerNeste(famDato).getDato();
    const perioder: UttakPeriodeDto[] = [];

    if ((erMorUfør || bareFarMedmorHarRett) && aktivitetsfriKvote) {
        // Mor har ikke rett til foreldrepenger her (hun er enten ufør eller har ingen rett i det hele
        // tatt), så det gir ikke mening å be henne dokumentere aktivitet. Vi foreslår derfor kun den
        // mindre kvoten uten aktivitetskrav (aktivitetsfri kvote), og lar den større kvoten med
        // aktivitetskrav ligge i reserve som brukeren selv kan velge å ta i bruk.
        const periode: UttakPeriodeDto = {
            fom: getTidsperiodeString(førsteUttaksdag, aktivitetsfriKvote.dager).fom,
            tom: getTidsperiodeString(førsteUttaksdag, aktivitetsfriKvote.dager).tom,
            søker: {
                forelder: 'FAR_MEDMOR',
                kontoType: 'FORELDREPENGER',
                morsAktivitet: 'IKKE_OPPGITT',
                flerbarnsdager: false,
            },
        };

        perioder.push(periode);
    } else if (farOgFar) {
        // NB: I motsetning til de andre grenene her bruker vi bevisst aktivitetsfriKvote og
        // morsAktivitet: 'IKKE_OPPGITT'. Når begge foreldrene er fedre finnes det ingen «mor» hvis
        // aktivitet kan dokumenteres, så et ordinært aktivitetskrav-basert konto (FORELDREPENGER)
        // gir ikke mening her – se VIS_AKTIVITETSKRAV_FELT i feltSynlighet.ts. Dette er trolig en egen,
        // gyldig forretningsregel og ikke del av «skal ikke foreslå aktivitetsfri kvote automatisk»-fiksen.
        const periode: UttakPeriodeDto = {
            fom: getTidsperiodeString(førsteUttaksdag, aktivitetsfriKvote!.dager).fom,
            tom: getTidsperiodeString(førsteUttaksdag, aktivitetsfriKvote!.dager).tom,
            søker: {
                forelder: 'FAR_MEDMOR',
                kontoType: 'FORELDREPENGER',
                morsAktivitet: 'IKKE_OPPGITT',
                flerbarnsdager: false,
            },
        };
        perioder.push(periode);
    } else {
        // Aktivitetsfri kvote (foreldrepenger uten aktivitetskrav) skal ikke brukes i det foreslåtte forslaget
        // her, siden dette er en kvote brukeren selv bør velge å bruke, ikke noe vi automatisk foreslår.
        const periode: UttakPeriodeDto = {
            fom: getTidsperiodeString(førsteUttaksdag, foreldrepengerKonto.dager).fom,
            tom: getTidsperiodeString(førsteUttaksdag, foreldrepengerKonto.dager).tom,
            søker: {
                forelder: 'FAR_MEDMOR',
                kontoType: 'FORELDREPENGER',
                flerbarnsdager: false,
            },
        };

        perioder.push(periode);
    }

    return perioder;
};

const ikkeDeltUttakAdopsjonMor = ({
    famDato,
    foreldrepengerKonto,
}: {
    famDato: string;
    foreldrepengerKonto: KontoDto;
}): UttakPeriodeDto[] => {
    const førsteUttaksdag = Uttaksdagen.denneEllerNeste(famDato).getDato();
    const periode: UttakPeriodeDto = {
        fom: getTidsperiodeString(førsteUttaksdag, foreldrepengerKonto.dager).fom,
        tom: getTidsperiodeString(førsteUttaksdag, foreldrepengerKonto.dager).tom,
        søker: {
            forelder: 'MOR',
            kontoType: 'FORELDREPENGER',
            flerbarnsdager: false,
        },
    };

    return [periode];
};

const ikkeDeltUttakAdopsjon = ({
    famDato,
    erFarEllerMedmor,
    foreldrepengerKonto,
    erMorUfør,
    aktivitetsfriKvote,
    bareFarMedmorHarRett,
    farOgFar,
}: {
    famDato: string;
    erFarEllerMedmor: boolean;
    foreldrepengerKonto: KontoDto;
    erMorUfør: boolean | undefined;
    aktivitetsfriKvote: KontoDto | undefined;
    bareFarMedmorHarRett: boolean;
    farOgFar: boolean;
}) => {
    if (erFarEllerMedmor) {
        return ikkeDeltUttakAdopsjonFarMedmor({
            famDato,
            foreldrepengerKonto,
            erMorUfør,
            aktivitetsfriKvote,
            bareFarMedmorHarRett,
            farOgFar,
        });
    } else {
        return ikkeDeltUttakAdopsjonMor({ famDato, foreldrepengerKonto });
    }
};

const ikkeDeltUttakFødselMor = ({
    famDato,
    foreldrepengerKonto,
    foreldrePengerFørFødselKonto,
    startdato,
}: {
    famDato: string;
    foreldrepengerKonto: KontoDto;
    foreldrePengerFørFødselKonto: KontoDto | undefined;
    startdato?: string;
}): UttakPeriodeDto[] => {
    const førsteUttaksdagEtterFødsel = Uttaksdagen.denneEllerNeste(famDato).getDato();
    // Når ingen startdato er valgt eksplisitt (t.d. planlegger-appen, som ikke har noe
    // eget oppstartsvalg-steg), bruker vi samme standard som før: 3 uker (15 uttaksdager)
    // før fødsel/termin.
    const standardStartdatoFørFødsel =
        Uttaksdagen.denne(førsteUttaksdagEtterFødsel).getDatoAntallUttaksdagerTidligere(15);
    const valgtStartdato = Uttaksdagen.denneEllerNeste(startdato ?? standardStartdatoFørFødsel).getDato();
    // Mor kan velge en oppstartsdato som ligger før fødselsdatoen (t.d. tre uker før termin/fødsel).
    // Da skal FORELDREPENGER_FØR_FØDSEL dekke perioden fra den valgte startdatoen og frem til fødselen.
    const starterFørFødsel = dayjs(valgtStartdato).isBefore(førsteUttaksdagEtterFødsel, 'd');

    const perioder: UttakPeriodeDto[] = [];

    if (foreldrePengerFørFødselKonto !== undefined && starterFørFødsel) {
        const periodeFørFødsel: UttakPeriodeDto = {
            fom: valgtStartdato,
            tom: Uttaksdagen.forrige(førsteUttaksdagEtterFødsel).getDato(),
            søker: {
                forelder: 'MOR',
                kontoType: 'FORELDREPENGER_FØR_FØDSEL',
                flerbarnsdager: false,
            },
        };

        perioder.push(periodeFørFødsel);
    }

    const foreldrepengerFom = starterFørFødsel ? førsteUttaksdagEtterFødsel : valgtStartdato;
    const antallDagerIForeldrepenger = getTidsperiodeString(foreldrepengerFom, foreldrepengerKonto.dager);

    const foreldrepengerPeriode: UttakPeriodeDto = {
        fom: antallDagerIForeldrepenger.fom,
        tom: antallDagerIForeldrepenger.tom,
        søker: {
            forelder: 'MOR',
            kontoType: 'FORELDREPENGER',
            flerbarnsdager: false,
        },
    };

    perioder.push(foreldrepengerPeriode);

    return [...perioder].sort(sorterUttakPerioder);
};

const ikkeDeltUttakFødselFarMedmor = ({
    famDato,
    foreldrepengerKonto,
    erMorUfør,
    aktivitetsfriKvote,
    bareFarMedmorHarRett,
    erAleneOmOmsorg,
    farOgFar,
    startdato,
}: {
    famDato: string;
    foreldrepengerKonto: KontoDto;
    erMorUfør: boolean | undefined;
    aktivitetsfriKvote: KontoDto | undefined;
    bareFarMedmorHarRett: boolean;
    erAleneOmOmsorg: boolean;
    farOgFar: boolean;
    startdato?: string;
}): UttakPeriodeDto[] => {
    const startDato = Uttaksdagen.denneEllerNeste(startdato ?? famDato).getDato();
    const perioder: UttakPeriodeDto[] = [];

    if ((erMorUfør || bareFarMedmorHarRett) && aktivitetsfriKvote) {
        // Mor har ikke rett til foreldrepenger her (hun er enten ufør eller har ingen rett i det hele
        // tatt), så det gir ikke mening å be henne dokumentere aktivitet. Vi foreslår derfor kun den
        // mindre kvoten uten aktivitetskrav (aktivitetsfri kvote), og lar den større kvoten med
        // aktivitetskrav ligge i reserve som brukeren selv kan velge å ta i bruk.
        const periode: UttakPeriodeDto = {
            fom: getTidsperiodeString(startDato, aktivitetsfriKvote.dager).fom,
            tom: getTidsperiodeString(startDato, aktivitetsfriKvote.dager).tom,
            søker: {
                forelder: 'FAR_MEDMOR',
                kontoType: 'FORELDREPENGER',
                morsAktivitet: 'IKKE_OPPGITT',
                flerbarnsdager: false,
            },
        };

        perioder.push(periode);
    } else {
        if (farOgFar && !erAleneOmOmsorg) {
            // NB: I motsetning til de andre grenene her bruker vi bevisst aktivitetsfriKvote og
            // morsAktivitet: 'IKKE_OPPGITT'. Når begge foreldrene er fedre finnes det ingen «mor» hvis
            // aktivitet kan dokumenteres, så et ordinært aktivitetskrav-basert konto (FORELDREPENGER)
            // gir ikke mening her – se VIS_AKTIVITETSKRAV_FELT i feltSynlighet.ts. Dette er trolig en egen,
            // gyldig forretningsregel og ikke del av «skal ikke foreslå aktivitetsfri kvote automatisk»-fiksen.
            const periode: UttakPeriodeDto = {
                fom: getTidsperiodeString(startDato, aktivitetsfriKvote!.dager).fom,
                tom: getTidsperiodeString(startDato, aktivitetsfriKvote!.dager).tom,
                søker: {
                    forelder: 'FAR_MEDMOR',
                    kontoType: 'FORELDREPENGER',
                    morsAktivitet: 'IKKE_OPPGITT',
                    flerbarnsdager: false,
                },
            };
            perioder.push(periode);
        } else {
            // Aktivitetsfri kvote (foreldrepenger uten aktivitetskrav) skal ikke brukes i det foreslåtte
            // forslaget her, siden dette er en kvote brukeren selv bør velge å bruke, ikke noe vi
            // automatisk foreslår.
            const periode: UttakPeriodeDto = {
                fom: getTidsperiodeString(startDato, foreldrepengerKonto.dager).fom,
                tom: getTidsperiodeString(startDato, foreldrepengerKonto.dager).tom,
                søker: {
                    forelder: 'FAR_MEDMOR',
                    kontoType: 'FORELDREPENGER',
                    flerbarnsdager: false,
                },
            };

            perioder.push(periode);
        }
    }

    return [...perioder].sort(sorterUttakPerioder);
};

const ikkeDeltUttakFødsel = ({
    famDato,
    erFarEllerMedmor,
    foreldrepengerKonto,
    foreldrePengerFørFødselKonto,
    erMorUfør,
    aktivitetsfriKvote,
    bareFarMedmorHarRett,
    erAleneOmOmsorg,
    farOgFar,
    startdato,
}: {
    famDato: string;
    erFarEllerMedmor: boolean;
    foreldrepengerKonto: KontoDto;
    foreldrePengerFørFødselKonto: KontoDto | undefined;
    erMorUfør: boolean | undefined;
    aktivitetsfriKvote: KontoDto | undefined;
    bareFarMedmorHarRett: boolean;
    erAleneOmOmsorg: boolean;
    farOgFar: boolean;
    startdato?: string;
}) => {
    if (erFarEllerMedmor) {
        return ikkeDeltUttakFødselFarMedmor({
            famDato,
            foreldrepengerKonto,
            erMorUfør,
            aktivitetsfriKvote,
            bareFarMedmorHarRett,
            erAleneOmOmsorg,
            farOgFar,
            startdato,
        });
    } else {
        return ikkeDeltUttakFødselMor({
            famDato,
            foreldrepengerKonto,
            foreldrePengerFørFødselKonto,
            startdato,
        });
    }
};

interface IkkeDeltUttakParams {
    situasjon: Situasjon;
    famDato: string;
    erFarEllerMedmor: boolean;
    tilgjengeligeStønadskvoter: KontoDto[];
    erMorUfør: boolean | undefined;
    bareFarMedmorHarRett: boolean;
    erAleneOmOmsorg: boolean;
    farOgFar: boolean;
    startdato?: string;
}

export const ikkeDeltUttak = ({
    situasjon,
    famDato,
    erFarEllerMedmor,
    tilgjengeligeStønadskvoter,
    erMorUfør,
    bareFarMedmorHarRett,
    erAleneOmOmsorg,
    startdato,
    farOgFar,
}: IkkeDeltUttakParams): UttakPeriodeDto[] => {
    const foreldrepengerKonto = tilgjengeligeStønadskvoter.find((konto) => konto.konto === 'FORELDREPENGER');
    const foreldrePengerFørFødselKonto = tilgjengeligeStønadskvoter.find(
        (konto) => konto.konto === 'FORELDREPENGER_FØR_FØDSEL',
    );
    const aktivitetsfriKvote = tilgjengeligeStønadskvoter.find((konto) => konto.konto === 'AKTIVITETSFRI_KVOTE');

    if (situasjon === 'adopsjon') {
        return ikkeDeltUttakAdopsjon({
            famDato,
            erFarEllerMedmor,
            foreldrepengerKonto: foreldrepengerKonto!,
            erMorUfør,
            aktivitetsfriKvote,
            bareFarMedmorHarRett,
            farOgFar,
        });
    }
    return ikkeDeltUttakFødsel({
        famDato,
        erFarEllerMedmor,
        foreldrepengerKonto: foreldrepengerKonto!,
        foreldrePengerFørFødselKonto,
        erMorUfør,
        aktivitetsfriKvote,
        bareFarMedmorHarRett,
        erAleneOmOmsorg,
        farOgFar,
        startdato,
    });
};

const getTidsperiodeString = (fom: string, uttaksdager: number): Tidsperiode => {
    return {
        fom,
        tom: Uttaksdagen.denne(fom).getDatoAntallUttaksdagerSenere(uttaksdager - 1),
    };
};
